import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { getAdminUser, getAdminRoleDisplayName } from '@/admin/services/adminAuthService';
import { Shield, Bell, Key, Database, Download } from 'lucide-react';

const AdminSettings: React.FC = () => {
  const adminUser = getAdminUser();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your admin account and portal preferences
        </p>
      </div>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Account Information
          </CardTitle>
          <CardDescription>
            Your admin account details and permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Email</Label>
              <p className="text-sm font-medium">{adminUser?.email}</p>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {adminUser && getAdminRoleDisplayName(adminUser.role)}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <p className="text-sm font-medium">
                {adminUser?.department || 'Not specified'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>User ID</Label>
              <p className="text-sm font-mono text-muted-foreground">
                {adminUser?.sub}
              </p>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <h4 className="text-sm font-medium mb-3">Permissions</h4>
            <div className="flex flex-wrap gap-2">
              {adminUser?.permissions.map((permission) => (
                <Badge key={permission} variant="outline">
                  {permission}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Configure how you receive admin notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-notifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive important updates via email
              </p>
            </div>
            <Switch id="email-notifications" defaultChecked />
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="high-value-alerts">High-Value Customer Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when customers reach earnings milestones
              </p>
            </div>
            <Switch id="high-value-alerts" defaultChecked />
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="security-alerts">Security Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Receive alerts for suspicious admin activities
              </p>
            </div>
            <Switch id="security-alerts" defaultChecked />
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="daily-summary">Daily Summary</Label>
              <p className="text-sm text-muted-foreground">
                Get a daily summary of platform metrics
              </p>
            </div>
            <Switch id="daily-summary" />
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Manage your account security preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-3">Two-Factor Authentication</h4>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">2FA Status</p>
                <p className="text-sm text-muted-foreground">
                  Required for all admin accounts
                </p>
              </div>
              <Badge variant="default">Enabled</Badge>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-3">Session Settings</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
                <Input
                  id="session-timeout"
                  type="number"
                  defaultValue="60"
                  className="w-20"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="remember-device">Remember This Device</Label>
                  <p className="text-sm text-muted-foreground">
                    Skip 2FA on trusted devices
                  </p>
                </div>
                <Switch id="remember-device" />
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex justify-between">
            <Button variant="outline">Change Password</Button>
            <Button variant="outline" className="text-red-600">
              Revoke All Sessions
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Management
          </CardTitle>
          <CardDescription>
            Export and manage your admin activity data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Export Audit Logs</p>
              <p className="text-sm text-muted-foreground">
                Download your admin activity history
              </p>
            </div>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
          
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Export Settings</p>
              <p className="text-sm text-muted-foreground">
                Backup your notification and security preferences
              </p>
            </div>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex justify-end gap-2">
        <Button variant="outline">Cancel</Button>
        <Button>Save Changes</Button>
      </div>
    </div>
  );
};

export default AdminSettings;